import { CSSObject } from '@emotion/react';
import theme from './theme';

const { color, radius } = theme;

const stickyTop: CSSObject = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  display: 'flow-root',
  margin: '0 -16px',
  padding: '0 16px 12px',
  background: color.background,
  boxShadow: `0 -24px 0 ${color.background}`,
};

const tabs: CSSObject = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  display: 'flex',
  margin: '0 -16px 12px',
  padding: '0 16px',
  background: color.background,
  boxShadow: `inset 0 -1px 0 ${color.grey100}, 0 -24px 0 ${color.background}`,
};

const tab = (active: boolean): CSSObject => ({
  flex: 1,
  height: 48,
  border: 'none',
  background: 'transparent',
  fontSize: 15,
  fontWeight: active ? 700 : 500,
  color: active ? color.text : color.grey400,
  boxShadow: active ? `inset 0 -2px 0 ${color.text}` : 'none',
  cursor: 'pointer',
});

const section: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const card: CSSObject = {
  position: 'relative',
  background: color.white,
  borderRadius: radius.lg,
};

const cardHeader: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '14px 16px 4px',
  fontSize: 16,
  fontWeight: 700,
  color: color.text,
};

const textButton: CSSObject = {
  flex: 'none',
  padding: '6px 8px',
  margin: '-6px -8px',
  border: 'none',
  background: 'transparent',
  fontSize: 14,
  fontWeight: 500,
  color: color.info,
  cursor: 'pointer',
};

const infoList: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  padding: '4px 16px 8px',
};

const infoRow: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  fontSize: 14,
  borderBottom: `1px solid ${color.grey75}`,
  ':last-of-type': {
    borderBottom: 'none',
  },
  dt: {
    flex: 'none',
    color: color.textSub,
  },
  dd: {
    minWidth: 0,
    color: color.text,
    textAlign: 'right',
    wordBreak: 'keep-all',
    overflowWrap: 'anywhere',
  },
};

const list: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  background: color.white,
  borderRadius: radius.lg,
  overflow: 'hidden',
};

const listItem: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  width: '100%',
  padding: '14px 16px',
  border: 'none',
  borderBottom: `1px solid ${color.grey75}`,
  background: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
  ':last-of-type': {
    borderBottom: 'none',
  },
  ':active': {
    background: color.grey50,
  },
};

const listItemStatic: CSSObject = {
  ...listItem,
  boxSizing: 'border-box',
  cursor: 'default',
  ':active': {
    background: 'transparent',
  },
};

const listRow: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
};

const listDate: CSSObject = {
  flex: 'none',
  fontSize: 13,
  color: color.textMuted,
};

type Tone = 'info' | 'success' | 'error' | 'grey';

const TONES: Record<Tone, [string, string]> = {
  info: [color.info, color.infoBg],
  success: [color.success, color.successBg],
  error: [color.error, color.errorBg],
  grey: [color.grey600, color.grey75],
};

const tag = (tone: Tone): CSSObject => ({
  flex: 'none',
  padding: '2px 6px',
  borderRadius: radius.sm / 2,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.5,
  color: TONES[tone][0],
  background: TONES[tone][1],
});

const empty: CSSObject = {
  padding: '40px 0',
  textAlign: 'center',
  fontSize: 14,
  color: color.textMuted,
};

const sentinel: CSSObject = {
  height: 1,
};

export type { Tone };
export {
  stickyTop,
  tabs,
  tab,
  section,
  card,
  cardHeader,
  textButton,
  infoList,
  infoRow,
  list,
  listItem,
  listItemStatic,
  listRow,
  listDate,
  tag,
  empty,
  sentinel,
};
